import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Visibility as VisibilityIcon,
  NoteAlt as NoteAltIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Assignment as AssignmentIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import {
  tienePermisosProgramaCompras,
  puedeEditarProgramaFacturacion,
} from 'lib/seguridad/permisosPorModulo';
import { FP, fpInputSx } from 'components/financepro';
import PrintListado from 'utils/PrintListado';

/** Misma clave de programa que RegistroCompra. */
const PROGRAMA_REGISTRO_COMPRAS = 'registrocompras';

const ESTADOS_FILTRO = [
  { value: '', label: 'Todos' },
  { value: 'A', label: 'Abiertas' },
  { value: 'C', label: 'Cerradas' },
];

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtFecha = (f) => {
  if (!f) return '—';
  const s = String(f).slice(0, 10);
  const [y, m, d] = s.split('-');
  if (!y || !m || !d) return s;
  return `${d}/${m}/${y}`;
};

const chipEstadoOcSx = (status) => {
  if (status === 'C') {
    return { bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary };
  }
  return { bgcolor: FP.color.accentBlueSoft, color: FP.color.accentBlue };
};

const chipEstadoRecepcionSx = (nombre) => {
  const n = (nombre || '').toLowerCase();
  if (n.includes('cerrada')) {
    return { bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary };
  }
  if (n.includes('recibida')) {
    return { bgcolor: FP.color.successSoft || '#E8F5E9', color: FP.color.success || '#2E7D32' };
  }
  if (n.includes('parcial')) {
    return { bgcolor: FP.color.warningSoft || '#FFF8E1', color: FP.color.warning || '#F57C00' };
  }
  return { bgcolor: FP.color.accentBlueSoft, color: FP.color.accentBlue };
};

const stickyHeadProps = {
  position: 'sticky',
  top: 0,
  zIndex: 2,
  '& td': {
    border: 'none',
    borderBottom: `2px solid ${FP.color.border}`,
    bgcolor: FP.color.surfaceAlt,
  },
};

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const filterHeaderSx = {
  px: { xs: 2, md: 3 },
  py: 1.6,
  borderBottom: `1px solid ${FP.color.border}`,
  bgcolor: '#FAFBFE',
  display: 'flex',
  alignItems: 'center',
  gap: 1.2,
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.74rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  bgcolor: FP.color.surfaceAlt,
  borderBottom: `1px solid ${FP.color.border}`,
  py: 1.2,
};

const btnPrimarySx = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 1.4,
  bgcolor: FP.color.navy,
  color: '#FFFFFF',
  boxShadow: 'none',
  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: FP.color.surfaceAlt, color: FP.color.textMuted },
};

const btnOutlinedSx = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 1.4,
  borderColor: FP.color.border,
  color: FP.color.textPrimary,
  bgcolor: '#FFFFFF',
  '&:hover': { borderColor: FP.color.borderStrong, bgcolor: FP.color.surfaceAlt },
};

/**
 * Seguimiento de órdenes de compra: listado, impresión, detalle de recepción y notas.
 */
const Detallecompras = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const {
    nombreNegocio,
    usuarioLog,
    claveLog,
    roleDetalles,
    direccion_empresa: direccionEmpresaCtx,
    rnc_empresa: rncEmpresaCtx,
  } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const permisoLeer = useMemo(
    () => tienePermisosProgramaCompras(roleDetalles, PROGRAMA_REGISTRO_COMPRAS),
    [roleDetalles]
  );
  const permisoModificar = useMemo(
    () =>
      puedeEditarProgramaFacturacion(roleDetalles, PROGRAMA_REGISTRO_COMPRAS) ||
      tienePermisosProgramaCompras(roleDetalles, PROGRAMA_REGISTRO_COMPRAS),
    [roleDetalles]
  );

  const [ordenes, setOrdenes] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [datosEmpresa, setDatosEmpresa] = useState(null);

  const [detalleAbierto, setDetalleAbierto] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);

  const [notaAbierta, setNotaAbierta] = useState(false);
  const [ordenNota, setOrdenNota] = useState(null);
  const [textoNota, setTextoNota] = useState('');
  const [guardandoNota, setGuardandoNota] = useState(false);

  const credenciales = () => ({
    id_empresa: selectedCompany,
    usuario: usuarioLog || '',
    password: claveLog || '',
  });

  /**
   * Datos de empresa para el encabezado de PrintListado.
   * Prioridad: configuración de factura; fallback a contexto de login / selectedCompany.
   */
  const cargarDatosEmpresa = useCallback(async () => {
    if (!permisoLeer || !selectedCompany?.db_empresa) return;

    const nombreCtx =
      typeof nombreNegocio === 'string' && nombreNegocio.trim() && nombreNegocio !== 'false'
        ? nombreNegocio.trim()
        : '';
    const direccionCtx =
      typeof direccionEmpresaCtx === 'string' && direccionEmpresaCtx.trim()
        ? direccionEmpresaCtx.trim()
        : '';
    const rncCtx =
      typeof rncEmpresaCtx === 'string' && rncEmpresaCtx.trim() ? rncEmpresaCtx.trim() : '';
    const fallback = {
      nombre:
        selectedCompany?.name ||
        selectedCompany?.empresa ||
        selectedCompany?.nombreEmpresa ||
        nombreCtx ||
        'Empresa',
      direccion:
        selectedCompany?.direccion_empresa ||
        selectedCompany?.direccionEmpresa ||
        selectedCompany?.direccion ||
        direccionCtx,
      telefono:
        selectedCompany?.telefono ||
        selectedCompany?.telefonoEmpresa ||
        selectedCompany?.telefono_empresa ||
        '',
      rnc: selectedCompany?.rnc_empresa || selectedCompany?.rncEmpresa || selectedCompany?.rnc || rncCtx,
      email: selectedCompany?.email || selectedCompany?.emailEmpresa || '',
    };

    try {
      const response = await fetch(servidor + '/api/facturabackend_obtenerconfiguracion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tipo_documento: 'Factura',
        }),
      });
      const config = response.ok
        ? (await response.json().catch(() => null))?.configuracion || null
        : null;

      setDatosEmpresa({
        nombre:
          (config?.nombreEmpresa || '').trim() ||
          fallback.nombre,
        direccion: (config?.direccionEmpresa || '').trim() || fallback.direccion || '',
        telefono: (config?.telefonoEmpresa || '').trim() || fallback.telefono || '',
        rnc: (config?.rncEmpresa || '').trim() || fallback.rnc || '',
        email: (config?.emailEmpresa || '').trim() || fallback.email || '',
      });
    } catch {
      setDatosEmpresa(fallback);
    }
  }, [
    permisoLeer,
    selectedCompany,
    usuarioLog,
    claveLog,
    servidor,
    nombreNegocio,
    direccionEmpresaCtx,
    rncEmpresaCtx,
  ]);

  const fetchOrdenes = async () => {
    const body = {
      ...credenciales(),
      anomesfacturacion: selectedCompany?.anomesfacturacion,
    };
    if (filtroStatus) body.status = filtroStatus;
    if (fechaDesde) body.fecha_desde = fechaDesde;
    if (fechaHasta) body.fecha_hasta = fechaHasta;
    const response = await fetch(servidor + '/api/comprasbackend_listarcompras', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (response.ok) {
      const data = await response.json();
      setOrdenes(data.ordenes || []);
    } else {
      const err = await response.json().catch(() => ({}));
      showError(err.Error || 'Error al listar órdenes de compra');
    }
  };

  const listarOrdenes = async () => {
    if (!permisoLeer || !selectedCompany?.db_empresa) return;
    setCargando(true);
    try {
      await fetchOrdenes();
    } catch (e) {
      showError('Error de conexión');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (permisoLeer) {
      listarOrdenes();
      cargarDatosEmpresa();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permisoLeer, selectedCompany]);

  const ordenesFiltradas = useMemo(() => {
    const q = (busqueda || '').trim().toLowerCase();
    if (!q) return ordenes;
    return ordenes.filter((o) => {
      const id = String(o.id ?? '');
      const suplidor = String(o.suplidor_nombre || o.suplidor || '').toLowerCase();
      const obs = String(o.observacion || '').toLowerCase();
      const estado = String(o.estado_recepcion || '').toLowerCase();
      return id.includes(q) || suplidor.includes(q) || obs.includes(q) || estado.includes(q);
    });
  }, [ordenes, busqueda]);

  /** Período del listado: siempre refleja Fecha desde / Fecha hasta de los filtros. */
  const periodoImpresion = useMemo(() => {
    const desde = (fechaDesde || '').trim();
    const hasta = (fechaHasta || '').trim();
    if (!desde && !hasta) {
      return 'Período: Todas las fechas (sin filtro de rango)';
    }
    return `Período: Del ${desde ? fmtFecha(desde) : '...'} al ${hasta ? fmtFecha(hasta) : '...'}`;
  }, [fechaDesde, fechaHasta]);

  const pieImpresion = useMemo(() => {
    const estadoLabel = ESTADOS_FILTRO.find((e) => e.value === filtroStatus)?.label || 'Todos';
    const partes = [
      `Total órdenes: ${ordenesFiltradas.length}`,
      `Estado OC: ${estadoLabel}`,
    ];
    if ((busqueda || '').trim()) {
      partes.push(`Búsqueda: ${(busqueda || '').trim()}`);
    }
    partes.push(`Impreso: ${fmtFecha(new Date().toISOString().slice(0, 10))}`);
    return partes.join(' | ');
  }, [ordenesFiltradas.length, filtroStatus, busqueda]);

  const columnasPrint = [
    { id: 'id', label: 'Nº orden', align: 'center' },
    { id: 'fecha_print', label: 'Fecha', align: 'center' },
    { id: 'suplidor_nombre', label: 'Suplidor' },
    { id: 'tipocompra', label: 'Tipo' },
    { id: 'status_print', label: 'Estado OC', align: 'center' },
    { id: 'estado_recepcion', label: 'Recepción', align: 'center' },
    { id: 'total_print', label: 'Total', align: 'right' },
    { id: 'observacion', label: 'Notas' },
  ];

  const datosPrint = useMemo(
    () =>
      ordenesFiltradas.map((o) => {
        const total = (o.items || []).reduce(
          (s, it) => s + (parseFloat(it.cantidad) || 0) * (parseFloat(it.precio) || 0),
          0
        );
        return {
          ...o,
          fecha_print: fmtFecha(o.fecha || o.fecha_str),
          status_print: o.status === 'C' ? 'Cerrada' : 'Abierta',
          total_print: `$${fmtMoney(total)}`,
          observacion: o.observacion || '',
        };
      }),
    [ordenesFiltradas]
  );

  const abrirDetalle = async (orden) => {
    if (!permisoLeer || !selectedCompany?.db_empresa) return;
    setCargandoDetalle(true);
    setDetalleAbierto(true);
    setDetalle(null);
    try {
      const response = await fetch(servidor + '/api/comprasbackend_detallecompra', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credenciales(),
          anomesfacturacion: selectedCompany?.anomesfacturacion,
          compra_id: orden.id,
          solo_recepcion: false,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        const det = data.detalle || {};
        // Preferir cantidades recibidas del listado (ya vienen mapeadas)
        const itemsListado = orden.items || [];
        const items = (det.items || []).map((it, idx) => {
          const fromList = itemsListado.find((x) => x.codigo === it.codigo || x.item === it.codigo) || itemsListado[idx];
          return {
            ...it,
            recibido: it.recibido ?? fromList?.recibido ?? 0,
            pendiente: it.pendiente ?? fromList?.pendiente ?? Math.max(0, (it.cantidad || 0) - (it.recibido || 0)),
          };
        });
        setDetalle({
          ...det,
          items,
          id: orden.id,
          suplidor: orden.suplidor,
          suplidor_nombre: orden.suplidor_nombre || det.cliente_nombre,
          observacion: orden.observacion ?? det.observaciones ?? '',
          estado_recepcion: det.estado_recepcion || orden.estado_recepcion,
          status: det.status || orden.status,
        });
      } else {
        const err = await response.json().catch(() => ({}));
        showError(err.Error || 'Error al cargar el detalle');
        setDetalleAbierto(false);
      }
    } catch (e) {
      showError('Error de conexión');
      setDetalleAbierto(false);
    } finally {
      setCargandoDetalle(false);
    }
  };

  const abrirNota = (orden) => {
    setOrdenNota(orden);
    setTextoNota(orden.observacion || '');
    setNotaAbierta(true);
  };

  const guardarNota = async () => {
    if (!permisoModificar) {
      showError('No tiene permiso para modificar notas');
      return;
    }
    if (!ordenNota) return;
    setGuardandoNota(true);
    try {
      const response = await fetch(servidor + '/api/comprasbackend_actualizarnota', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credenciales(),
          compra_id: ordenNota.id,
          nota: textoNota,
        }),
      });
      if (response.ok) {
        showSuccess('Nota guardada');
        setNotaAbierta(false);
        setOrdenNota(null);
        await listarOrdenes();
        if (detalle && detalle.id === ordenNota.id) {
          setDetalle((prev) => (prev ? { ...prev, observacion: textoNota, observaciones: textoNota } : prev));
        }
      } else {
        const err = await response.json().catch(() => ({}));
        showError(err.Error || 'Error al guardar la nota');
      }
    } catch (e) {
      showError('Error de conexión');
    } finally {
      setGuardandoNota(false);
    }
  };

  const totalOrden = (orden) =>
    (orden.items || []).reduce(
      (s, it) => s + (parseFloat(it.cantidad) || 0) * (parseFloat(it.precio) || 0),
      0
    );

  return (
    <>
      <Card elevation={0} sx={{ ...cardShellSx, mb: 2.5 }}>
        <Box sx={filterHeaderSx}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.2,
              bgcolor: FP.color.accentBlueSoft,
              color: FP.color.accentBlue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FilterListIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
            Filtros de seguimiento
          </Typography>
          <Box sx={{ flex: 1 }} />
          <PrintListado
            title={
              datosEmpresa?.nombre ||
              selectedCompany?.name ||
              selectedCompany?.empresa ||
              'Empresa'
            }
            empresa={datosEmpresa}
            reportTitle="Listado de seguimiento de órdenes de compra"
            subtitle={periodoImpresion}
            columns={columnasPrint}
            data={datosPrint}
            emptyMessage="No hay órdenes para imprimir"
            printButtonLabel="Imprimir listado"
            printButtonProps={{ sx: btnPrimarySx }}
            footer={pieImpresion}
          />
          <Button
            startIcon={<RefreshIcon />}
            onClick={listarOrdenes}
            disabled={cargando}
            variant="outlined"
            sx={{ ...btnOutlinedSx, ml: 1 }}
          >
            Actualizar
          </Button>
        </Box>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Grid container spacing={2.5} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Fecha desde"
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Fecha hasta"
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth sx={fpInputSx}>
                <InputLabel shrink>Estado OC</InputLabel>
                <Select
                  value={filtroStatus}
                  label="Estado OC"
                  onChange={(e) => setFiltroStatus(e.target.value)}
                >
                  {ESTADOS_FILTRO.map((e) => (
                    <MenuItem key={e.value || 'all'} value={e.value}>
                      {e.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Buscar"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Nº, suplidor, nota…"
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button fullWidth variant="contained" onClick={listarOrdenes} disabled={cargando} sx={btnPrimarySx}>
                Buscar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card elevation={0} sx={cardShellSx}>
        <Box sx={filterHeaderSx}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.2,
              bgcolor: FP.color.accentBlueSoft,
              color: FP.color.accentBlue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AssignmentIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
            Órdenes de compra
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.85rem' }}>
            {cargando ? 'Cargando…' : `${ordenesFiltradas.length} registro(s)`}
          </Typography>
        </Box>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ maxHeight: { xs: 420, md: 560 }, borderRadius: 0 }}
        >
          <Table size="small" stickyHeader>
            <TableBody>
              <TableRow sx={stickyHeadProps}>
                <TableCell sx={cellHeadSx}>Nº orden</TableCell>
                <TableCell sx={cellHeadSx}>Fecha</TableCell>
                <TableCell sx={cellHeadSx}>Suplidor</TableCell>
                <TableCell sx={cellHeadSx}>Tipo</TableCell>
                <TableCell sx={cellHeadSx}>Estado OC</TableCell>
                <TableCell sx={cellHeadSx}>Recepción</TableCell>
                <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
                  Total
                </TableCell>
                <TableCell sx={cellHeadSx}>Notas</TableCell>
                <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                  Acciones
                </TableCell>
              </TableRow>
              {ordenesFiltradas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <Typography sx={{ color: FP.color.textMuted }}>
                      {cargando ? 'Cargando órdenes…' : 'No hay órdenes de compra para mostrar'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                ordenesFiltradas.map((o) => (
                  <TableRow key={o.id} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.88rem' }}>
                        {o.id}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.84rem', color: FP.color.textPrimary }}>
                      {fmtFecha(o.fecha || o.fecha_str)}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: '0.84rem',
                        color: FP.color.textPrimary,
                        maxWidth: 220,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {o.suplidor_nombre || o.suplidor || '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.84rem', color: FP.color.textSecondary }}>
                      {o.tipocompra || '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={o.status === 'C' ? 'Cerrada' : 'Abierta'}
                        sx={{ fontWeight: 700, ...chipEstadoOcSx(o.status) }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={o.estado_recepcion || 'Pendiente'}
                        sx={{ fontWeight: 700, ...chipEstadoRecepcionSx(o.estado_recepcion) }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.84rem' }}>
                      ${fmtMoney(totalOrden(o))}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: '0.8rem',
                        color: FP.color.textSecondary,
                        maxWidth: 160,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={o.observacion || ''}
                    >
                      {o.observacion || '—'}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        title="Ver detalle"
                        onClick={() => abrirDetalle(o)}
                        sx={{ color: FP.color.accentBlue }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        title="Notas"
                        onClick={() => abrirNota(o)}
                        sx={{ color: FP.color.navy }}
                      >
                        <NoteAltIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Dialog detalle orden */}
      <Dialog
        open={detalleAbierto}
        onClose={() => setDetalleAbierto(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            pr: 1,
          }}
        >
          <ShoppingCartIcon sx={{ color: FP.color.accentBlue, fontSize: 22 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: FP.color.textPrimary }}>
              Orden de compra Nº {detalle?.id ?? detalle?.numero ?? '…'}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: FP.color.textSecondary }}>
              Información general y mercancía recibida
            </Typography>
          </Box>
          <IconButton onClick={() => setDetalleAbierto(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
          {cargandoDetalle || !detalle ? (
            <Typography sx={{ color: FP.color.textMuted, py: 4, textAlign: 'center' }}>
              Cargando detalle…
            </Typography>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 2.5, mt: 0.5 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography sx={{ fontSize: '0.72rem', color: FP.color.textMuted, fontWeight: 700 }}>
                    FECHA
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>{fmtFecha(detalle.fecha)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography sx={{ fontSize: '0.72rem', color: FP.color.textMuted, fontWeight: 700 }}>
                    VENCIMIENTO
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {fmtFecha(detalle.fechaVencimiento || detalle.fechaentrega)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography sx={{ fontSize: '0.72rem', color: FP.color.textMuted, fontWeight: 700 }}>
                    ESTADO OC
                  </Typography>
                  <Chip
                    size="small"
                    label={detalle.status === 'C' ? 'Cerrada' : 'Abierta'}
                    sx={{ fontWeight: 700, mt: 0.3, ...chipEstadoOcSx(detalle.status) }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography sx={{ fontSize: '0.72rem', color: FP.color.textMuted, fontWeight: 700 }}>
                    RECEPCIÓN
                  </Typography>
                  <Chip
                    size="small"
                    label={detalle.estado_recepcion || 'Pendiente'}
                    sx={{
                      fontWeight: 700,
                      mt: 0.3,
                      ...chipEstadoRecepcionSx(detalle.estado_recepcion),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: '0.72rem', color: FP.color.textMuted, fontWeight: 700 }}>
                    SUPLIDOR
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {detalle.suplidor_nombre || detalle.cliente_nombre || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: '0.72rem', color: FP.color.textMuted, fontWeight: 700 }}>
                    TIPO
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {detalle.tipoDocumento || detalle.tipocompra || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: '0.72rem', color: FP.color.textMuted, fontWeight: 700 }}>
                    NOTAS
                  </Typography>
                  <Typography sx={{ fontWeight: 500, color: FP.color.textPrimary }}>
                    {detalle.observacion || detalle.observaciones || 'Sin notas'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 2 }} />

              <Typography sx={{ fontWeight: 700, mb: 1.2, color: FP.color.textPrimary }}>
                Ítems y recepción
              </Typography>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 1.4 }}
              >
                <Table size="small">
                  <TableBody>
                    <TableRow sx={stickyHeadProps}>
                      <TableCell sx={cellHeadSx}>Código</TableCell>
                      <TableCell sx={cellHeadSx}>Descripción</TableCell>
                      <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
                        Pedido
                      </TableCell>
                      <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
                        Recibido
                      </TableCell>
                      <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
                        Pendiente
                      </TableCell>
                      <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
                        Precio
                      </TableCell>
                      <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
                        Subtotal
                      </TableCell>
                    </TableRow>
                    {(detalle.items || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                          Sin ítems
                        </TableCell>
                      </TableRow>
                    ) : (
                      (detalle.items || []).map((it, idx) => {
                        const cant = parseFloat(it.cantidad) || 0;
                        const rec = parseFloat(it.recibido) || 0;
                        const pend =
                          it.pendiente != null ? parseFloat(it.pendiente) || 0 : Math.max(0, cant - rec);
                        const precio = parseFloat(it.precio) || 0;
                        return (
                          <TableRow key={`${it.codigo}-${idx}`} hover>
                            <TableCell sx={{ fontSize: '0.84rem' }}>{it.codigo || '—'}</TableCell>
                            <TableCell sx={{ fontSize: '0.84rem' }}>{it.descripcion || '—'}</TableCell>
                            <TableCell align="right">{cant}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: FP.color.success || '#2E7D32' }}>
                              {rec}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: pend > 0 ? (FP.color.warning || '#F57C00') : FP.color.textSecondary }}>
                              {pend}
                            </TableCell>
                            <TableCell align="right">${fmtMoney(precio)}</TableCell>
                            <TableCell align="right">${fmtMoney(it.subtotal ?? cant * precio)}</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, mt: 2 }}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '0.75rem', color: FP.color.textMuted, fontWeight: 700 }}>
                    SUBTOTAL
                  </Typography>
                  <Typography sx={{ fontWeight: 700 }}>${fmtMoney(detalle.subtotal)}</Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '0.75rem', color: FP.color.textMuted, fontWeight: 700 }}>
                    TOTAL
                  </Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: FP.color.navy }}>
                    ${fmtMoney(detalle.total)}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${FP.color.border}` }}>
          {detalle && (
            <Button
              startIcon={<NoteAltIcon />}
              variant="outlined"
              onClick={() =>
                abrirNota({
                  id: detalle.id || detalle.numero,
                  observacion: detalle.observacion || detalle.observaciones || '',
                })
              }
              sx={btnOutlinedSx}
            >
              Editar notas
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setDetalleAbierto(false)} sx={btnOutlinedSx} variant="outlined">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog notas */}
      <Dialog
        open={notaAbierta}
        onClose={() => !guardandoNota && setNotaAbierta(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
          }}
        >
          <NoteAltIcon sx={{ color: FP.color.navy, fontSize: 22 }} />
          <Typography sx={{ fontWeight: 800, flex: 1 }}>
            Notas · OC Nº {ordenNota?.id}
          </Typography>
          <IconButton onClick={() => setNotaAbierta(false)} size="small" disabled={guardandoNota}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <TextField
            fullWidth
            multiline
            rows={5}
            label="Notas de la orden"
            value={textoNota}
            onChange={(e) => setTextoNota(e.target.value)}
            placeholder="Escriba notas o comentarios sobre esta orden de compra…"
            disabled={!permisoModificar || guardandoNota}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 1, ...fpInputSx }}
            inputProps={{ maxLength: 500 }}
            helperText={`${(textoNota || '').length}/500`}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${FP.color.border}` }}>
          <Button
            variant="outlined"
            onClick={() => setNotaAbierta(false)}
            disabled={guardandoNota}
            sx={btnOutlinedSx}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={guardarNota}
            disabled={!permisoModificar || guardandoNota}
            sx={btnPrimarySx}
          >
            {guardandoNota ? 'Guardando…' : 'Guardar nota'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Detallecompras;
